import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Boxes, LogIn, AlertCircle, Sun, Moon } from "lucide-react";

export default function Login() {
  const { user, login, error } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (user && user !== false) nav("/"); }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(email, password);
    setSubmitting(false);
    if (ok) nav("/");
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Left brand panel — desktop only */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-foreground text-background p-12 flex-col justify-between">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1587293852726-70cdb56c2866?crop=entropy&cs=srgb&fm=jpg&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "luminosity",
          }}
        />
        <div className="absolute inset-0 bg-black/60 mix-blend-multiply" />

        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 bg-background text-foreground flex items-center justify-center rounded-sm">
            <Boxes className="w-6 h-6" />
          </div>
          <div>
            <div className="font-bold text-xl tracking-tight" style={{ fontFamily: "Space Grotesk" }}>WarenPro</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-background/70 font-semibold">Warenwirtschaft</div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <div className="text-[10px] uppercase tracking-[0.3em] text-background/60 font-semibold mb-4">Multi-Channel ERP</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4" style={{ fontFamily: "Space Grotesk" }}>
            Lager. Verkauf.<br />Abrechnung.
          </h1>
          <p className="text-background/80 text-base leading-relaxed">
            Verwalten Sie Lager, Lieferanten und Verkäufe für Lagerverkauf, B2B und Online — mit Live-Anbindung an
            discount-jaeger.de (WooCommerce) sowie eBay und Merkandi.
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-4 text-xs">
          <div>
            <div className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Space Grotesk" }}>3</div>
            <div className="text-background/60 uppercase tracking-[0.15em] font-semibold text-[10px]">Verkaufskanäle</div>
          </div>
          <div>
            <div className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Space Grotesk" }}>3</div>
            <div className="text-background/60 uppercase tracking-[0.15em] font-semibold text-[10px]">Marktplätze</div>
          </div>
          <div>
            <div className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Space Grotesk" }}>500</div>
            <div className="text-background/60 uppercase tracking-[0.15em] font-semibold text-[10px]">Paletten</div>
          </div>
        </div>
      </div>

      {/* Right login form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 relative">
        {/* Theme toggle top-right on the form panel */}
        <button
          onClick={toggle}
          data-testid="theme-toggle-login"
          aria-label={theme === "dark" ? "Zu hellem Modus wechseln" : "Zu dunklem Modus wechseln"}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-sm border border-border bg-card hover:bg-accent text-foreground transition-colors"
          title={theme === "dark" ? "Heller Modus" : "Dunkler Modus"}
        >
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>

        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 bg-foreground text-background flex items-center justify-center rounded-sm">
              <Boxes className="w-5 h-5" />
            </div>
            <span className="font-bold text-lg" style={{ fontFamily: "Space Grotesk" }}>WarenPro</span>
          </div>

          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-semibold mb-2">Anmeldung</div>
          <h2 className="text-3xl font-bold text-foreground mb-2 tracking-tight" style={{ fontFamily: "Space Grotesk" }}>Willkommen zurück</h2>
          <p className="text-sm text-muted-foreground mb-8">Melden Sie sich an, um auf Ihr ERP zuzugreifen.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-semibold text-foreground uppercase tracking-[0.1em] mb-1.5">E-Mail</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-input bg-card text-foreground rounded-sm px-3 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none transition"
                placeholder="ihre@email.de"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground uppercase tracking-[0.1em] mb-1.5">Passwort</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-input bg-card text-foreground rounded-sm px-3 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none transition"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-sm" data-testid="login-error">
                <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                <span className="text-sm text-destructive">{error}</span>
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-primary text-primary-foreground hover:opacity-90 py-3 rounded-sm text-sm font-semibold flex items-center justify-center gap-2 transition disabled:opacity-60"
            >
              <LogIn className="w-4 h-4" />
              {submitting ? "Wird angemeldet…" : "Anmelden"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
